import L from "leaflet";
import { ORTOMOSAICO_PUBLIC_URI_PREFIX } from "../../../../constantes";
import { storageTilesSasReadService } from "../../../comun/services/storage/StorageTilesSasReadService";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";

/** Tile transparente (1x1 gif) para tiles faltantes: evita el icono de "imagen rota". */
const TRANSPARENT_TILE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/**
 * Toggle de ortomosaico para el mapa. Construye y gestiona su propia capa de
 * tiles a partir de la URI, anexándole el SAS de lectura (mismo contenedor
 * privado que conteo), y registra su propio botón en el contenedor. Arranca
 * visible: pide el SAS de entrada y marca el botón "activo" recién cuando la
 * capa ya está montada (no antes, para no mostrarlo activo sin tiles).
 * Usar: const ctrl = new OrtomosaicoToggleControl(uriOrtomosaico);
 *       ctrl.init(container, map);
 */
export class OrtomosaicoControl {
  /** URI base de los tiles del ortomosaico. */
  private _uri: string | null;
  /** Mapa Leaflet (se setea en `init`). */
  private _map!: L.Map;
  /** Capa de tiles del ortomosaico (o null si está apagado). */
  private _layer: L.TileLayer | null = null;
  /** Botón del control. */
  private _btn: HTMLButtonElement | null = null;
  /** Estado del toggle (arranca visible). */
  private _visible = true;
  /** Handler del click del botón (nombrado para poder removerlo). */
  private _onClick = (): void => void this._toggle();

  /** `get()` y no `getRequired()`: sin finca seleccionada esto no es un error,
   *  es "no hay ortomosaico que mostrar".
   *
   *  Lo necesita el wizard de registro de finca, que monta este control en los
   *  pasos 4 y 5 cuando se está EDITANDO una finca ya creada. Ahí la finca está
   *  seleccionada, pero el mismo código corre también al crear una desde cero, y
   *  `getRequired()` tiraba. `init()` ya no hace nada sin URI, así que el control
   *  se apaga solo: no registra el botón ni pide el SAS. */
  constructor() {
    const finca = serviceProvider.getFincaSelectedService().get();
    this._uri = finca?.uriOrtomosaico ?? null;
  }

  /** Registra el botón en el contenedor y dispara la carga de la capa
   *  (estado inicial: visible).
   *
   *  @param conLabel Botón GRANDE con el texto "Ortomosaico" al lado del ícono
   *   (`sx-map-btn--lg`, el mismo formato que las herramientas del wizard) y
   *   **sin** `title`: con el nombre a la vista el tooltip es ruido.
   *
   *   Por defecto queda el botón chico de sólo ícono con tooltip, que es lo que
   *   necesitan los otros módulos: ahí vive en la barra superior, junto a otros
   *   íconos sueltos, y un texto no entraría. Lo pide el wizard, donde el botón
   *   comparte columna con Dividir/Unir/Dibujar/Editar/Borrar —todos con label—
   *   y el ícono solo se veía fuera de lugar. */
  init(parent: HTMLElement, map: L.Map, conLabel = false): void {
    if (!this._uri) return;

    this._map = map;

    const btn = L.DomUtil.create(
      "button",
      conLabel ? "sx-map-btn sx-map-btn--lg" : "sx-map-btn",
    ) as HTMLButtonElement;
    btn.innerHTML = conLabel
      ? '<i class="ph ph-map-trifold"></i><span>Ortomosaico</span>'
      : '<i class="ph ph-map-trifold"></i>';
    if (!conLabel) btn.title = "Ortomosaico";
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", this._onClick);
    this._btn = btn;

    parent.appendChild(btn);
    void this._addLayer();
  }

  /** Remueve la capa de tiles y el listener del botón. */
  dispose(): void {
    this._removeLayer();
    if (this._btn) L.DomEvent.off(this._btn, "click", this._onClick);
    this._btn = null;
  }

  /** Alterna el ortomosaico: agrega o remueve su capa de tiles. */
  private async _toggle(): Promise<void> {
    this._visible = !this._visible;
    if (this._visible) {
      await this._addLayer();
    } else {
      this._removeLayer();
    }
  }

  /** Pide el SAS (salvo que la URI ya sea de un contenedor público, ver
   *  `ORTOMOSAICO_PUBLIC_URI_PREFIX`), arma la capa de tiles y la agrega al
   *  mapa; si falla el SAS, revierte el estado del botón. */
  private async _addLayer(): Promise<void> {
    if (!this._uri) return;

    if (this._uri.startsWith(ORTOMOSAICO_PUBLIC_URI_PREFIX)) {
      this._layer = L.tileLayer(this._uri, {
        maxZoom: 20,
        maxNativeZoom: 19,
        errorTileUrl: TRANSPARENT_TILE,
      }).addTo(this._map);
      this._btn?.classList.add("sx-map-btn--active");
      return;
    }

    try {
      const sas = await storageTilesSasReadService.getAsync();
      // El usuario pudo apagar el ortomosaico mientras se resolvía el SAS.
      if (!this._visible) return;

      this._layer = L.tileLayer(`${this._uri}?${sas.sasToken}`, {
        maxZoom: 20,
        maxNativeZoom: 19,
        errorTileUrl: TRANSPARENT_TILE,
      }).addTo(this._map);
      this._btn?.classList.add("sx-map-btn--active");
    } catch {
      // Falló la obtención del SAS: revertir el estado del botón.
      this._visible = false;
      this._btn?.classList.remove("sx-map-btn--active");
    }
  }

  /** Remueve la capa del ortomosaico y desmarca el botón. */
  private _removeLayer(): void {
    this._layer?.remove();
    this._layer = null;
    this._btn?.classList.remove("sx-map-btn--active");
  }
}
